import React, { useCallback, useMemo, useState } from 'react'
import ObjectDesktop from './desktop/desktop-object'
import ObjectMobile from './mobile/mobile-object'
import {
    EntitySectionContent,
    ExposeMobileWrapper,
    ExposeDesktopWrapper,
    ShowMoreButton,
    ShowMoreIcon,
    ObjectSeparatorMobile,
    ObjectSeparatorDesktop,
    ChooseDate,
} from './styles'
import { ComponentSeparator, ExposeSectionTitleWrapper, StyledGhostButton } from '../style'
import { EntitySectionPosition, SECTION_IDS } from '../constants'
import { Typography } from 'react-components'
import { useTranslation } from '../../../hooks/locale'
import { SearchbarFieldsReturn } from '../hooks/use-searchbar'
import PulsatingDots from '../pulsating-dots'
import { FormattedPropertyData } from '../../../models/property-details-page/property-details-model'
import { LivePricing } from '../../../hooks/combo-pricing'
import get from 'lodash/get'
import AnalyticsService from '../../../services/analytics-service'
import { pdpPageCategories } from '../../../services/analytics-service/categories'
import { pdpPageActions } from '../../../services/analytics-service/actions'

type EntitySectionProps = {
    data: FormattedPropertyData[]
    totalUnits: number | null
    searchbar: SearchbarFieldsReturn
    hasMoreUnits: boolean
    isUnitsLoading: boolean
    loadMoreUnits: () => void
    onSelectDatesButtonClick: () => void
    selectedIndex: number
    setSelectedIndex: React.Dispatch<React.SetStateAction<number>>
    pricingDetails: Map<string, LivePricing>
}

const EntitySection: React.FC<EntitySectionProps> = ({
    data,
    totalUnits,
    searchbar,
    hasMoreUnits,
    isUnitsLoading,
    loadMoreUnits,
    onSelectDatesButtonClick,
    selectedIndex,
    setSelectedIndex,
    pricingDetails,
}) => {
    const { t } = useTranslation()
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [SideBarPopup, setSideBarPopup] = useState<React.ComponentType<any> | null>(null)
    const [sidebarLoadingIndex, setSidebarLoadingIndex] = useState<number | null>(null)

    const loadSideBarPopup = useCallback(async () => {
        const module = await import('./pop-up')
        setSideBarPopup(() => module.default)
        setSidebarLoadingIndex(null)
    }, [])

    const handleSidebarOpen = useCallback(
        async (index: number) => {
            setTimeout(() => {
                AnalyticsService.trackEvent({
                    category: pdpPageCategories.PDP_PAGE_COMBO,
                    action: pdpPageActions.COMBO_LISTING_POPUP_OPEBED,
                })
            }, 0)
            if (!SideBarPopup) {
                setSidebarLoadingIndex(index)
                await loadSideBarPopup()
            }
            setSelectedIndex(index)
            setIsSidebarOpen(true)
        },
        [SideBarPopup, loadSideBarPopup, setSelectedIndex, setIsSidebarOpen, setSidebarLoadingIndex],
    )

    const handleSidebarClose = useCallback(() => {
        setIsSidebarOpen(false)
    }, [])

    const showMoreUnits = useCallback(
        (position: EntitySectionPosition) => {
            setTimeout(() => {
                AnalyticsService.trackEvent({
                    category: pdpPageCategories.PDP_PAGE_COMBO,
                    action: pdpPageActions.SHOW_MORE_ACCOMMODATIONS_CLICKED,
                    label: position,
                })
            }, 0)
            loadMoreUnits()
        },
        [loadMoreUnits],
    )

    const handleChangeIndex = useCallback(
        (newIndex: number, position: EntitySectionPosition) => {
            const boundedIndex = Math.max(0, Math.min(newIndex, data.length - 1))

            if (boundedIndex !== selectedIndex) {
                setTimeout(() => {
                    AnalyticsService.trackEvent({
                        category: pdpPageCategories.PDP_PAGE_COMBO,
                        action: pdpPageActions.SELECTED_UNIT_CHANGE,
                        label: position,
                    })
                }, 0)
            }
            setSelectedIndex(boundedIndex)
        },
        [selectedIndex, setSelectedIndex, data.length],
    )

    const onSelectDatesButton = useCallback(() => {
        setTimeout(() => {
            AnalyticsService.trackEvent({
                category: pdpPageCategories.PDP_PAGE_COMBO,
                action: pdpPageActions.SELECT_DATES_BUTTON_CLICKED,
            })
        }, 0)
        onSelectDatesButtonClick()
    }, [onSelectDatesButtonClick])

    const renderObjects = useCallback(
        (Component: typeof ObjectDesktop | typeof ObjectMobile, Separator: React.ElementType) => {
            if (data.length === 0 && !isUnitsLoading) {
                return (
                    <Typography variant="subtitle1" color="tfwBlack99" fontWeight="highlight">
                        {t('entitySection.noUnitsAvailable')}
                    </Typography>
                )
            }

            return data.map((unitData: FormattedPropertyData, index: number) => (
                <React.Fragment key={unitData.id}>
                    <Component
                        onClick={() => handleSidebarOpen(index)}
                        onChangeIndex={() =>
                            handleChangeIndex(index, EntitySectionPosition.COMBO_LISTING_ENTITY_SECTION)
                        }
                        data={unitData}
                        isSelected={index === selectedIndex}
                        price={get(pricingDetails.get(unitData.id)?.pricingDetails, 'formattedPrice', '- -')}
                        isPriceLoading={pricingDetails.get(unitData.id)?.isLoading || false}
                        isSidebarLoading={index === sidebarLoadingIndex}
                    />
                    {index < data.length - 1 && <Separator />}
                </React.Fragment>
            ))
        },
        [
            data,
            isUnitsLoading,
            pricingDetails,
            sidebarLoadingIndex,
            selectedIndex,
            handleSidebarOpen,
            handleChangeIndex,
            t,
        ],
    )

    const renderedDesktopObjects = useMemo(() => {
        return renderObjects(ObjectDesktop, ObjectSeparatorDesktop)
    }, [renderObjects])

    const renderedMobileObjects = useMemo(() => {
        return renderObjects(ObjectMobile, ObjectSeparatorMobile)
    }, [renderObjects])

    return (
        <section id={SECTION_IDS.ENTITY_SECTION_ID}>
            <ExposeSectionTitleWrapper>
                <Typography variant="h6" fontWeight="semiBold" element="h2">
                    {t('entitySection.title')}
                </Typography>
            </ExposeSectionTitleWrapper>

            {searchbar.departure === null && data.length === 0 ? (
                <ChooseDate>
                    <StyledGhostButton isPrimary isContentWidth buttonSize="medium" onClick={onSelectDatesButton}>
                        <Typography variant="subtitle2" fontWeight="highlight">
                            {t('selectDatesAndGuests')}
                        </Typography>
                    </StyledGhostButton>
                </ChooseDate>
            ) : (
                <div>
                    <EntitySectionContent>
                        <ExposeDesktopWrapper>{renderedDesktopObjects}</ExposeDesktopWrapper>
                        <ExposeMobileWrapper>{renderedMobileObjects}</ExposeMobileWrapper>
                        {isUnitsLoading ? (
                            <PulsatingDots />
                        ) : (
                            hasMoreUnits && (
                                <ShowMoreButton
                                    onClick={() => showMoreUnits(EntitySectionPosition.COMBO_LISTING_ENTITY_SECTION)}
                                >
                                    <Typography variant="button" color="tfwHyperLink" fontWeight={'regular'}>
                                        {t('entitySection.showMore')}
                                    </Typography>
                                    <ShowMoreIcon />
                                </ShowMoreButton>
                            )
                        )}
                    </EntitySectionContent>
                    {!!SideBarPopup && !!data[selectedIndex] && (
                        <SideBarPopup
                            data={data}
                            totalUnits={totalUnits}
                            isSidebarOpen={isSidebarOpen}
                            selectedIndex={selectedIndex}
                            onChangeIndex={handleChangeIndex}
                            onClose={handleSidebarClose}
                            pricingDetails={pricingDetails}
                            searchbar={searchbar}
                            loadMoreUnits={() => showMoreUnits(EntitySectionPosition.COMBO_LISTING_POPUP)}
                            isUnitsLoading={isUnitsLoading}
                            hasMoreUnits={hasMoreUnits}
                        />
                    )}
                </div>
            )}
            <ComponentSeparator />
        </section>
    )
}

export default EntitySection
